import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from 'reactstrap';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import ParticlesAuth from '../AuthenticationInner/ParticlesAuth';
import { authApi } from '../../services/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [passwordShow, setPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('No reset token provided. Please request a new password reset link.');
        setVerifying(false);
        return;
      }

      try {
        const data = await authApi.verifyResetToken(token);

        if (data.valid) {
          setTokenValid(true);
          setMaskedEmail(data.email);
        } else {
          setError(data.message || 'Invalid or expired reset token.');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to verify reset token. Please try again.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[0-9]/, 'Password must contain at least one number')
        .required('Please enter your new password'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Please confirm your new password'),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);

        await authApi.resetPassword(token!, values.password);

        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err: any) {
        setError(err.message || 'Failed to reset password. Please try again.');
      } finally {
        setLoading(false);
      }
    },
  });

  document.title = 'Reset Password | Coderom';

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-flex align-items-center gap-2 text-decoration-none">
                      <img src="/logo-full-white.png" alt="Coderom" style={{ height: '50px', width: 'auto' }} />
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">
                    {/* Loading/Verifying State */}
                    {verifying ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p className="text-muted mt-3">Verifying reset token...</p>
                      </div>
                    ) : success ? (
                      /* Success State */
                      <div className="text-center">
                        <div className="mt-4 pt-2">
                          <div className="avatar-lg mx-auto">
                            <div className="avatar-title bg-success-subtle text-success display-5 rounded-circle">
                              <i className="ri-check-double-line"></i>
                            </div>
                          </div>
                        </div>
                        <Alert color="success" className="mt-4">
                          <h5 className="alert-heading">Password Reset Successful!</h5>
                          <p className="mb-0">
                            Your password has been reset successfully.
                            You will be redirected to the login page shortly.
                          </p>
                        </Alert>
                        <Link to="/login" className="btn btn-primary mt-3">
                          <i className="ri-login-box-line me-1"></i>
                          Go to Login
                        </Link>
                      </div>
                    ) : !tokenValid ? (
                      /* Invalid Token State */
                      <div className="text-center">
                        <div className="mt-4 pt-2">
                          <div className="avatar-lg mx-auto">
                            <div className="avatar-title bg-danger-subtle text-danger display-5 rounded-circle">
                              <i className="ri-error-warning-line"></i>
                            </div>
                          </div>
                        </div>
                        <Alert color="danger" className="mt-4">
                          <h5 className="alert-heading">Invalid Reset Link</h5>
                          <p className="mb-0">{error}</p>
                        </Alert>
                        <Link to="/forgot-password" className="btn btn-primary mt-3">
                          <i className="ri-mail-send-line me-1"></i>
                          Request New Reset Link
                        </Link>
                      </div>
                    ) : (
                      /* Reset Form */
                      <>
                        <div className="text-center mt-2">
                          <h5 className="text-primary">Create New Password</h5>
                          <p className="text-muted">
                            {maskedEmail && (
                              <>
                                Reset password for <strong>{maskedEmail}</strong>
                              </>
                            )}
                          </p>
                        </div>

                        {/* Error Alert */}
                        {error && (
                          <Alert color="danger" className="mt-3">
                            <i className="ri-error-warning-line me-2"></i>
                            {error}
                          </Alert>
                        )}

                        <div className="p-2 mt-4">
                          <Form
                            onSubmit={(e) => {
                              e.preventDefault();
                              validation.handleSubmit();
                              return false;
                            }}
                          >
                            {/* New Password */}
                            <div className="mb-3">
                              <Label className="form-label" htmlFor="password">
                                New Password
                              </Label>
                              <div className="position-relative auth-pass-inputgroup">
                                <Input
                                  name="password"
                                  type={passwordShow ? 'text' : 'password'}
                                  className="form-control pe-5"
                                  placeholder="Enter new password"
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  value={validation.values.password || ''}
                                  invalid={
                                    validation.touched.password && validation.errors.password
                                      ? true
                                      : false
                                  }
                                />
                                {validation.touched.password && validation.errors.password ? (
                                  <FormFeedback type="invalid">
                                    {validation.errors.password}
                                  </FormFeedback>
                                ) : null}
                                <button
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  type="button"
                                  onClick={() => setPasswordShow(!passwordShow)}
                                >
                                  <i className={passwordShow ? 'ri-eye-off-fill' : 'ri-eye-fill'}></i>
                                </button>
                              </div>
                            </div>

                            {/* Confirm Password */}
                            <div className="mb-3">
                              <Label className="form-label" htmlFor="confirmPassword">
                                Confirm Password
                              </Label>
                              <div className="position-relative auth-pass-inputgroup">
                                <Input
                                  name="confirmPassword"
                                  type={confirmPasswordShow ? 'text' : 'password'}
                                  className="form-control pe-5"
                                  placeholder="Confirm new password"
                                  onChange={validation.handleChange}
                                  onBlur={validation.handleBlur}
                                  value={validation.values.confirmPassword || ''}
                                  invalid={
                                    validation.touched.confirmPassword &&
                                    validation.errors.confirmPassword
                                      ? true
                                      : false
                                  }
                                />
                                {validation.touched.confirmPassword &&
                                validation.errors.confirmPassword ? (
                                  <FormFeedback type="invalid">
                                    {validation.errors.confirmPassword}
                                  </FormFeedback>
                                ) : null}
                                <button
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  type="button"
                                  onClick={() => setConfirmPasswordShow(!confirmPasswordShow)}
                                >
                                  <i className={confirmPasswordShow ? 'ri-eye-off-fill' : 'ri-eye-fill'}></i>
                                </button>
                              </div>
                            </div>

                            {/* Password Requirements */}
                            <div className="mb-4">
                              <p className="text-muted fs-12 mb-1">Password must contain:</p>
                              <ul className="list-unstyled fs-12 text-muted">
                                <li className={validation.values.password.length >= 8 ? 'text-success' : ''}>
                                  <i className={`ri-${validation.values.password.length >= 8 ? 'check' : 'close'}-line me-1`}></i>
                                  At least 8 characters
                                </li>
                                <li className={/[A-Z]/.test(validation.values.password) ? 'text-success' : ''}>
                                  <i className={`ri-${/[A-Z]/.test(validation.values.password) ? 'check' : 'close'}-line me-1`}></i>
                                  One uppercase letter
                                </li>
                                <li className={/[a-z]/.test(validation.values.password) ? 'text-success' : ''}>
                                  <i className={`ri-${/[a-z]/.test(validation.values.password) ? 'check' : 'close'}-line me-1`}></i>
                                  One lowercase letter
                                </li>
                                <li className={/[0-9]/.test(validation.values.password) ? 'text-success' : ''}>
                                  <i className={`ri-${/[0-9]/.test(validation.values.password) ? 'check' : 'close'}-line me-1`}></i>
                                  One number
                                </li>
                              </ul>
                            </div>

                            {/* Submit Button */}
                            <div className="mt-4">
                              <Button
                                color="primary"
                                disabled={loading}
                                className="btn btn-primary w-100"
                                type="submit"
                              >
                                {loading ? (
                                  <>
                                    <Spinner size="sm" className="me-2" />
                                    Resetting...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-lock-password-line me-1"></i>
                                    Reset Password
                                  </>
                                )}
                              </Button>
                            </div>
                          </Form>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Back to Login */}
                <div className="mt-4 text-center">
                  <p className="mb-0">
                    Remember your password?{' '}
                    <Link
                      to="/login"
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      Sign In
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default ResetPassword;
